import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import Decimal from 'decimal.js';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Modal, { ModalProps } from 'rsuite/lib/Modal';
import { z } from 'zod';
import { ButtonPrimary, Callout, InputBox } from '../../../../../components';
import { LoadingSpinerArea } from '../../../../../components/LoadingSpinner';
import { selectorSelectedDatapoints } from '../../../../../reducers/gerenciadorPrecos/selectedDatapoints';
import { selectorTotalElements } from '../../../../../reducers/gerenciadorPrecos/totalElements';
import { getTotalProducts } from '../../utils';
import { Icon } from '../QuickActionFilter';
import styles from './ModalAlterarPrecos.module.scss';

const MAX_PERCENTAGE_VALUE = 99.9;
const MAX_EXTENDED_PERCENTAGE_VALUE = 999.9;

const getMaxEditedValue = (editType: 'PRICE' | 'MARGIN' | 'COMPETITIVENESS', comportamentTypeEdit: 'VALUE' | 'INCREASE' | 'LOWER'): number | undefined => {
  if (editType === 'PRICE' && comportamentTypeEdit === 'VALUE') return undefined;
  if (editType === 'PRICE' && comportamentTypeEdit === 'INCREASE') return MAX_EXTENDED_PERCENTAGE_VALUE;
  if (editType === 'COMPETITIVENESS' && ['VALUE', 'INCREASE'].includes(comportamentTypeEdit)) return MAX_EXTENDED_PERCENTAGE_VALUE;
  return MAX_PERCENTAGE_VALUE;
};

const alterarPrecosSchema = z
  .object({
    editType: z.enum(['PRICE', 'MARGIN', 'COMPETITIVENESS'], {
      invalid_type_error: 'Selecione uma opção válida.',
      required_error: 'Por favor, selecione uma opção.',
    }),
    comportamentTypeEdit: z.enum(['VALUE', 'INCREASE', 'LOWER'], {
      invalid_type_error: 'Selecione uma opção válida.',
      required_error: 'Por favor, selecione uma opção.',
    }),
    editedValue: z
      .number({
        required_error: 'Por favor, preencha este campo.',
      })
      .positive('O valor deve ser maior que zero.'),
    roundPrice: z.boolean(),
    basePrice: z.enum(['SUGGESTED_PRICE', 'RETAIL_PRICE'], {
      invalid_type_error: 'Selecione uma opção válida.',
      required_error: 'Por favor, selecione uma opção.',
    }),
  })
  .superRefine(({ editType, comportamentTypeEdit, editedValue }, context) => {
    const maxEditedValue = getMaxEditedValue(editType, comportamentTypeEdit);

    if (maxEditedValue !== undefined && editedValue > maxEditedValue) {
      context.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: maxEditedValue,
        type: 'number',
        inclusive: true,
        message: `O valor máximo permitido é ${maxEditedValue}.`,
        path: ['editedValue'],
      });
    }
  });

const SUFFIX = {
  PRICE: {
    VALUE: undefined,
    INCREASE: '%',
    LOWER: '%',
  },
  MARGIN: {
    VALUE: '%',
    INCREASE: 'pp',
    LOWER: 'pp',
  },
  COMPETITIVENESS: {
    VALUE: '%',
    INCREASE: 'pp',
    LOWER: 'pp',
  },
} as const;

export type AlterarPrecosSchema = z.infer<typeof alterarPrecosSchema>;

export type ModalAlterarPrecosProps = {
  onSubmit: (formData: AlterarPrecosSchema) => void;
} & ModalProps;

export const ModalAlterarPrecos = ({ className, onSubmit, ...props }: ModalAlterarPrecosProps) => {
  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    watch,
    formState: { isDirty, isValid },
  } = useForm<AlterarPrecosSchema>({
    defaultValues: {
      editType: 'PRICE',
      comportamentTypeEdit: 'VALUE',
      roundPrice: true,
      basePrice: 'SUGGESTED_PRICE',
    },
    mode: 'onChange',
    resolver: zodResolver(alterarPrecosSchema),
  });

  const editType = watch('editType');
  const comportamentTypeEdit = watch('comportamentTypeEdit');
  const maxEditedValue = getMaxEditedValue(editType, comportamentTypeEdit);
  const isRelativeEdit = comportamentTypeEdit !== 'VALUE';
  const shouldHavePrefix = maxEditedValue === undefined;
  const precision = shouldHavePrefix ? 2 : 1;

  const normalizeEditedValue = (nextEditType: AlterarPrecosSchema['editType'], nextComportamentTypeEdit: AlterarPrecosSchema['comportamentTypeEdit']) => {
    const currentValue = getValues('editedValue');
    if (currentValue === undefined) return;

    const nextMaxEditedValue = getMaxEditedValue(nextEditType, nextComportamentTypeEdit);
    const nextPrecision = nextMaxEditedValue === undefined ? 2 : 1;
    const truncatedValue = new Decimal(currentValue).toDecimalPlaces(nextPrecision, Decimal.ROUND_DOWN).toNumber();
    const normalizedValue = nextMaxEditedValue === undefined ? truncatedValue : Math.min(truncatedValue, nextMaxEditedValue);

    setValue('editedValue', normalizedValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const resetBasePrice = () => setValue('basePrice', 'SUGGESTED_PRICE');

  const resetOpeningDefaults = () => {
    resetBasePrice();
    setValue('roundPrice', true);
  };

  const totalElements = useSelector(selectorTotalElements);

  const selectedDatapoints = useSelector(selectorSelectedDatapoints);

  const total = useMemo(() => getTotalProducts(totalElements, selectedDatapoints), [selectedDatapoints, totalElements]);
  return (
    <Modal
      className={classNames('info-modal', styles['modal-alterar-precos'], className)}
      {...props}
      onEnter={node => {
        resetOpeningDefaults();
        props.onEnter?.(node);
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header>
          <Modal.Title>Alterar o preço</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputBox.Label>
            Escolha como alterar os
            <strong>
              {` ${total} `}
              preços selecionados.
            </strong>
          </InputBox.Label>
          <div className={styles['input-wrapper']}>
            <Controller
              name="editType"
              control={control}
              render={({ field: { value, onChange } }) => (
                <InputBox className={styles['input-wrapper__edit-type']}>
                  <InputBox.Select
                    data={[
                      {
                        value: 'PRICE',
                        label: 'Preço',
                      },
                      {
                        value: 'MARGIN',
                        label: 'Margem',
                      },
                      {
                        value: 'COMPETITIVENESS',
                        label: 'Competitividade',
                      },
                    ]}
                    value={value}
                    onChange={nextEditType => {
                      const typedEditType = nextEditType as AlterarPrecosSchema['editType'];
                      onChange(typedEditType);
                      normalizeEditedValue(typedEditType, comportamentTypeEdit);
                    }}
                    searchable={false}
                    cleanable={false}
                  />
                </InputBox>
              )}
            />

            <Icon icon="MdArrowForward" className={styles['arrow-svg']} />

            <Controller
              name="comportamentTypeEdit"
              control={control}
              render={({ field: { value, onChange } }) => (
                <InputBox className={styles['input-wrapper__comportament-type']}>
                  <InputBox.Select
                    data={[
                      {
                        value: 'VALUE',
                        label: 'Valor',
                      },
                      {
                        value: 'INCREASE',
                        label: 'Aumentar',
                      },
                      {
                        value: 'LOWER',
                        label: 'Baixar',
                      },
                    ]}
                    value={value}
                    onChange={nextComportamentTypeEdit => {
                      const typedComportamentTypeEdit = nextComportamentTypeEdit as AlterarPrecosSchema['comportamentTypeEdit'];
                      onChange(typedComportamentTypeEdit);
                      normalizeEditedValue(editType, typedComportamentTypeEdit);
                      if (typedComportamentTypeEdit === 'VALUE') resetBasePrice();
                    }}
                    searchable={false}
                    cleanable={false}
                  />
                </InputBox>
              )}
            />

            <Controller
              name="editedValue"
              control={control}
              render={({ field: { value, onChange } }) => (
                <InputBox className={styles['input-wrapper__edited-value']}>
                  <InputBox.Numeric
                    value={value}
                    allowNegative={false}
                    allowEmpty={false}
                    precision={precision}
                    decimalSeparator=","
                    max={maxEditedValue}
                    rejectExceedingMax
                    onChangeEvent={onChange}
                    prefix={shouldHavePrefix ? 'R$' : undefined}
                    suffix={SUFFIX[editType][comportamentTypeEdit]}
                  />
                </InputBox>
              )}
            />
          </div>

          {isRelativeEdit && (
            <Controller
              name="basePrice"
              control={control}
              render={({ field: { value, onChange } }) => (
                <InputBox className={styles['base-price']}>
                  <InputBox.Label className={styles['base-price__label']}>Escolha a base de alteração</InputBox.Label>
                  <InputBox.RadioGroup className={styles['base-price__options']} value={value} onChange={onChange}>
                    <InputBox.Radio value="SUGGESTED_PRICE">Preço sugerido</InputBox.Radio>
                    <InputBox.Radio value="RETAIL_PRICE">Preço vigente</InputBox.Radio>
                  </InputBox.RadioGroup>
                </InputBox>
              )}
            />
          )}

          <Controller
            name="roundPrice"
            control={control}
            render={({ field: { value, onChange } }) => (
              <InputBox>
                <InputBox.Check checked={value} onChange={(_event, _value, checked) => onChange(checked)}>
                  Arredondar segundo preço psicológico
                </InputBox.Check>
              </InputBox>
            )}
          />

          <Callout className={styles.callout}>
            <Callout.Title>Atenção!</Callout.Title>
            <Callout.Description>Todos os preços selecionados serão alterados.</Callout.Description>
          </Callout>
        </Modal.Body>
        <Modal.Footer>
          <ButtonPrimary skin="blue" theme="ghost" type="reset" onClick={props.onHide}>
            Fechar
          </ButtonPrimary>
          <ButtonPrimary skin="blue" theme="filled" type="submit" disabled={!isDirty || !isValid}>
            alterar preços
          </ButtonPrimary>
        </Modal.Footer>
      </form>
      <LoadingSpinerArea height="100%" size="md" area="ipa/gerenciador/alterar-precos" />
    </Modal>
  );
};
